import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, collection, getDocs, query, Timestamp } from "firebase/firestore";
import { db, auth } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import goldMedal from "./assets/gold-medal.png";
import silverMedal from "./assets/silver-medal.png";
import bronzeMedal from "./assets/bronze-medal.png";
import DancerThumbnail from "./DancerThumbnail";
import { ClickableStudioName } from "./utils/ClickableNames";
import "./DancerThumbnail.css";

export default function CompetitionDetails() {
  const { competitionId } = useParams();
  const navigate = useNavigate();
  const [competitionData, setCompetitionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [starDancersCount, setStarDancersCount] = useState(0);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Check if user is admin
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setIsAdmin(currentUser?.email === "giannisaliev@gmail.com");
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const loadCompetitionData = async () => {
      try {
        setLoading(true);
        
        // Try to load from old structure first
        const oldDocRef = doc(db, "competitions", competitionId);
        const oldDoc = await getDoc(oldDocRef);
        
        let competition = null;
        
        if (oldDoc.exists()) {
          console.log("🔍 Found competition in old structure:", competitionId);
          competition = oldDoc.data();
        } else {
          console.log("🔍 Competition not found in old structure, checking new structure...");
          
          // Try to find in new structure
          const disciplinesSnapshot = await getDocs(collection(db, "disciplines"));
          
          for (const disciplineDoc of disciplinesSnapshot.docs) {
            const categoriesSnapshot = await getDocs(collection(db, "disciplines", disciplineDoc.id, "categories"));
            
            for (const categoryDoc of categoriesSnapshot.docs) {
              // Check direct competitions
              try {
                const compDoc = await getDoc(doc(db, "disciplines", disciplineDoc.id, "categories", categoryDoc.id, "competitions", competitionId));
                if (compDoc.exists()) {
                  console.log("🔍 Found competition in new structure (direct):", competitionId);
                  competition = compDoc.data();
                  break;
                }
              } catch (error) {
                // Continue searching
              }
              
              // Check dance-specific competitions
              try {
                const dancesSnapshot = await getDocs(collection(db, "disciplines", disciplineDoc.id, "categories", categoryDoc.id, "dances"));
                for (const danceDoc of dancesSnapshot.docs) {
                  try {
                    const compDoc = await getDoc(doc(db, "disciplines", disciplineDoc.id, "categories", categoryDoc.id, "dances", danceDoc.id, "competitions", competitionId));
                    if (compDoc.exists()) {
                      console.log("🔍 Found competition in new structure (dance-specific):", competitionId);
                      competition = compDoc.data();
                      break;
                    }
                  } catch (error) {
                    // Continue searching
                  }
                }
                if (competition) break;
              } catch (error) {
                // Continue searching
              }
            }
            if (competition) break;
          }
        }
        
        if (!competition) {
          setError("Competition not found");
          return;
        }
        
        // Calculate points for each result if not already calculated
        if (competition.results) {
          competition.results = competition.results.map(result => ({
            ...result,
            calculatedPoints: result.points || calculatePoints(result.place, competition.participants)
          }));
          
          // Sort by place
          competition.results.sort((a, b) => a.place - b.place);
        }
        
        setCompetitionData(competition);
        
        // Calculate star dancers (top 3 leaderboard dancers at competition date) - only for admin
        if (competition && isAdmin) {
          calculateStarDancers(competition);
        }
        
      } catch (error) {
        console.error("❌ Error loading competition data:", error);
        setError("Failed to load competition data");
      } finally {
        setLoading(false);
      }
    };
    
    if (competitionId) {
      loadCompetitionData();
    }
  }, [competitionId, isAdmin]);

  // Calculate points using the same formula as in UploadCompetition
  function calculatePoints(place, participants) {
    const raw = 321 * Math.exp(-0.077 * place) * (0.39 * Math.log(participants) + 0.13);
    return Math.round(raw);
  }

  // Format date
  function formatDate(timestamp) {
    const dateObj = timestamp instanceof Timestamp 
      ? timestamp.toDate() 
      : typeof timestamp === "string" 
        ? new Date(timestamp) 
        : timestamp;
    
    return dateObj.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric"
    });
  }

  // Format category for display
  function formatCategoryDisplay(category, dance, discipline) {
    if (discipline === "Latin" && dance) {
      return `${category} - ${dance}`;
    }
    
    if (discipline && discipline !== "Latin") {
      return `${category} (${discipline})`;
    }
    
    return category;
  }

  // Handle dancer name click to navigate to their profile
  const handleDancerClick = (dancerName) => {
    navigate(`/dancer/${encodeURIComponent(dancerName)}`);
  };

  // Calculate star dancers - those who were in top 3 of leaderboard at competition date
  const calculateStarDancers = async (competition) => {
    try {
      const competitionDate = competition.timestamp instanceof Timestamp 
        ? competition.timestamp.toDate() 
        : new Date(competition.timestamp);
      
      console.log("🌟 Calculating star dancers for competition on:", competitionDate.toLocaleDateString());
      
      // Get all competitions from both old and new structures before this competition's date
      const allCompetitions = [];
      
      // Get from old structure
      const oldCompsSnapshot = await getDocs(collection(db, "competitions"));
      oldCompsSnapshot.forEach(doc => {
        const comp = doc.data();
        const compDate = comp.timestamp instanceof Timestamp 
          ? comp.timestamp.toDate() 
          : new Date(comp.timestamp);
        
        // Only include competitions before or on the same date as our target competition
        if (compDate <= competitionDate) {
          allCompetitions.push({
            id: doc.id,
            ...comp,
            date: compDate
          });
        }
      });

      // Get from new structure
      const disciplinesSnapshot = await getDocs(collection(db, "disciplines"));
      for (const disciplineDoc of disciplinesSnapshot.docs) {
        const categoriesSnapshot = await getDocs(collection(db, "disciplines", disciplineDoc.id, "categories"));
        
        for (const categoryDoc of categoriesSnapshot.docs) {
          // Check direct competitions
          try {
            const directCompsSnapshot = await getDocs(collection(db, "disciplines", disciplineDoc.id, "categories", categoryDoc.id, "competitions"));
            directCompsSnapshot.forEach(doc => {
              const comp = doc.data();
              const compDate = comp.timestamp instanceof Timestamp 
                ? comp.timestamp.toDate() 
                : new Date(comp.timestamp);
              
              if (compDate <= competitionDate) {
                allCompetitions.push({
                  id: doc.id,
                  ...comp,
                  date: compDate
                });
              }
            });
          } catch (error) {
            // Category might not have direct competitions
          }
          
          // Check dance-specific competitions
          try {
            const dancesSnapshot = await getDocs(collection(db, "disciplines", disciplineDoc.id, "categories", categoryDoc.id, "dances"));
            for (const danceDoc of dancesSnapshot.docs) {
              const danceCompsSnapshot = await getDocs(collection(db, "disciplines", disciplineDoc.id, "categories", categoryDoc.id, "dances", danceDoc.id, "competitions"));
              danceCompsSnapshot.forEach(doc => {
                const comp = doc.data();
                const compDate = comp.timestamp instanceof Timestamp 
                  ? comp.timestamp.toDate() 
                  : new Date(comp.timestamp);
                
                if (compDate <= competitionDate) {
                  allCompetitions.push({
                    id: doc.id,
                    ...comp,
                    date: compDate
                  });
                }
              });
            }
          } catch (error) {
            // Category might not have dances
          }
        }
      }

      console.log("🌟 Found", allCompetitions.length, "competitions before this date");

      // Build leaderboard state at the competition date
      const dancerPoints = new Map();
      
      allCompetitions.forEach(comp => {
        if (comp.results) {
          comp.results.forEach(result => {
            const dancerName = result.dancerName;
            const points = result.points || 0;
            const multiplier = comp.multiplier || 1;
            const finalPoints = Math.round(points * multiplier);
            
            if (!dancerPoints.has(dancerName)) {
              dancerPoints.set(dancerName, []);
            }
            
            dancerPoints.get(dancerName).push({
              points: finalPoints,
              date: comp.date,
              compId: comp.id
            });
          });
        }
      });

      // Calculate top 4 points for each dancer (same as leaderboard logic)
      const leaderboard = [];
      dancerPoints.forEach((results, dancerName) => {
        // Sort by points descending and take top 4
        const top4 = results
          .sort((a, b) => b.points - a.points)
          .slice(0, 4);
        
        const totalPoints = top4.reduce((sum, r) => sum + r.points, 0);
        
        if (totalPoints > 0) {
          leaderboard.push({
            name: dancerName,
            totalPoints
          });
        }
      });

      // Sort leaderboard by total points
      leaderboard.sort((a, b) => b.totalPoints - a.totalPoints);
      
      // Get top 3 dancers
      const top3Dancers = leaderboard.slice(0, 3).map(d => d.name);
      console.log("🌟 Top 3 dancers at competition date:", top3Dancers);
      
      // Check how many of these top 3 dancers participated in this competition
      const participatingDancers = competition.results.map(r => r.dancerName);
      const starDancersPresent = top3Dancers.filter(star => 
        participatingDancers.includes(star)
      );
      
      console.log("🌟 Star dancers present in this competition:", starDancersPresent);
      setStarDancersCount(starDancersPresent.length);
      
    } catch (error) {
      console.error("❌ Error calculating star dancers:", error);
      setStarDancersCount(0);
    }
  };

  if (loading) return <div style={{ padding: "2rem", textAlign: "center" }}>Loading competition details...</div>;
  if (error) return <div style={{ padding: "2rem", textAlign: "center", color: "red" }}>Error: {error}</div>;
  if (!competitionData) return <div style={{ padding: "2rem", textAlign: "center" }}>Competition not found</div>;

  // Smart back navigation - go to competitions tab if coming from dancer profile
  const handleBackNavigation = () => {
    const returnToDancer = sessionStorage.getItem('returnToDancer');
    if (returnToDancer) {
      sessionStorage.removeItem('returnToDancer');
      navigate(`/dancer/${encodeURIComponent(returnToDancer)}#competitions`);
      return;
    }
    // Default back navigation
    navigate(-1);
  };

  return (
    <div style={{ padding: "2rem", maxWidth: "90vw", margin: "0 auto" }}>
      {/* Header */}
      <div style={{ marginBottom: "2rem" }}>
        <button
          onClick={handleBackNavigation}
          style={{
            backgroundColor: "#6c757d",
            color: "white",
            border: "none",
            borderRadius: "4px",
            padding: "0.5rem 1rem",
            marginBottom: "1rem",
            cursor: "pointer"
          }}
        >
          ← Back
        </button>
        <h1 style={{ margin: "0 0 0.5rem 0", color: "#333" }}>
          🏆 {competitionData.name || competitionId}
        </h1>
        <div style={{
          backgroundColor: "#f8f9fa",
          padding: "1rem",
          borderRadius: "8px",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "1rem",
          marginBottom: "2rem"
        }}>
          <div><strong>📅 Date:</strong> {formatDate(competitionData.timestamp)}</div>
          <div><strong>🎭 Category:</strong> {formatCategoryDisplay(competitionData.category, competitionData.dance, competitionData.discipline)}</div>
          <div><strong>👥 Participants:</strong> {competitionData.participants || competitionData.results?.length || 0}</div>
          <div><strong>📊 Multiplier:</strong> {competitionData.multiplier || 1}x</div>
        </div>
      </div>

      {/* Results Table */}
      <div style={{
        overflowX: "auto",
        backgroundColor: "white",
        borderRadius: "8px",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        margin: "0 -16px"
      }}>
        <table className="competition-table" style={{
          width: "100%",
          borderCollapse: "collapse",
          tableLayout: "fixed"
        }}>
          <thead>
            <tr style={{ backgroundColor: "#007aff", color: "white" }}>
              <th style={{ padding: "0.4rem 0.2rem", textAlign: "center", width: "16%", fontSize: "0.9rem" }}>Place</th>
              <th style={{ padding: "0.4rem 0.2rem", textAlign: "center", width: "68%", fontSize: "0.9rem" }}>Dancer</th>
              {/* Studio column only on desktop */}
              <th className="desktop-only" style={{ padding: "0.5rem", textAlign: "center", width: "25%" }}>Studio</th>
              <th style={{ padding: "0.4rem 0.2rem", textAlign: "center", width: "16%", fontSize: "0.9rem" }}>Points</th>
            </tr>
          </thead>
          <tbody>
            {competitionData.results && competitionData.results.length > 0 ? (
              competitionData.results.map((result, index) => {
                const finalPoints = Math.round(result.calculatedPoints * (competitionData.multiplier || 1));
                const isTopPosition = result.place <= 3;
                return (
                  <tr
                    key={index}
                    style={{
                      backgroundColor: index % 2 === 0 ? "#f8f9fa" : "white",
                      borderBottom: "1px solid #dee2e6"
                    }}
                  >
                    <td style={{
                      padding: "1rem",
                      fontWeight: "bold",
                      color: isTopPosition ? "#28a745" : "#333",
                      fontSize: isTopPosition ? "1.1em" : "1em",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem"
                    }}>
                      {result.place === 1
                        ? "🥇"
                        : result.place === 2
                        ? "🥈"
                        : result.place === 3
                        ? "🥉"
                        : result.place}
                      {(result.withdrawn || result.withdraw === "true" || result.withdraw === true) && (
                        <span 
                          style={{ 
                            color: "#dc3545", 
                            fontWeight: "bold", 
                            fontSize: "1.1em", 
                            marginLeft: "0.25rem" 
                          }} 
                          title="Dancer withdrew and didnt finish the competition"
                        >
                          W
                        </span>
                      )}
                    </td>
                    
                    <td style={{
                      padding: "0.5rem",
                      verticalAlign: "top"
                    }}>
                      <div className="dancer-with-thumbnail" style={{ display: "flex", alignItems: "center" }}>
                        <DancerThumbnail 
                          dancerName={result.dancerName} 
                          size={36} 
                          showPlaceholder={true}
                        />
                        <div style={{ marginLeft: "0.5rem" }}>
                          <div 
                            className="dancer-name-with-thumbnail"
                            style={{
                              cursor: "pointer",
                              color: "#007bff",
                              textDecoration: "underline"
                            }}
                            onClick={() => handleDancerClick(result.dancerName)}
                            title="Click to view dancer profile"
                          >
                            {result.dancerName}
                          </div>
                          {/* Studio info below name on mobile */}
                          <div className="mobile-only" style={{ color: "#666", fontSize: "0.95em", marginTop: "0.15rem" }}>
                            {result.studio ? (
                              <ClickableStudioName 
                                name={result.studio}
                                style={{ color: "#0066cc", textDecoration: "none" }}
                              >
                                {result.studio}
                              </ClickableStudioName>
                            ) : "-"}
                          </div>
                        </div>
                      </div>
                    </td>
                    {/* Studio column only on desktop */}
                    <td className="desktop-only" style={{ padding: "1rem", color: "#666" }}>
                      {result.studio ? (
                        <ClickableStudioName 
                          name={result.studio}
                          style={{ color: "#0066cc", textDecoration: "none" }}
                        >
                          {result.studio}
                        </ClickableStudioName>
                      ) : "-"}
                    </td>
                    {/* Points with multiplier column, renamed to Points */}
                    <td style={{
                      padding: "1rem",
                      textAlign: "center",
                      fontFamily: "monospace",
                      fontWeight: "bold",
                      color: "#28a745",
                      whiteSpace: "nowrap",
                      overflow: "visible"
                    }}>
                      {finalPoints}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="5" style={{
                  padding: "2rem",
                  textAlign: "center",
                  color: "#666",
                  fontStyle: "italic"
                }}>
                  No results available for this competition
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Summary Statistics */}
      {competitionData.results && competitionData.results.length > 0 && (
        <div style={{
          marginTop: "2rem",
          backgroundColor: "#f8f9fa",
          padding: "1.5rem",
          borderRadius: "8px"
        }}>
          <h3 style={{ marginTop: 0, color: "#333" }}>📈 Competition Statistics</h3>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "1rem"
          }}>
            <div>
              <strong>Total Participants:</strong> {competitionData.results.length}
            </div>
            {/* Star Dancers - Admin Only */}
            {isAdmin && (
              <div>
                <strong>⭐ Star Dancers Present:</strong> {starDancersCount}/3
                <div style={{ fontSize: "0.85em", color: "#666", marginTop: "0.25rem" }}>
                  (Top 3 leaderboard dancers at competition date) [Admin Only]
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
